import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useCustomerAuth from '../hooks/useCustomerAuth';
import useOtpResendCooldown from '../hooks/useOtpResendCooldown';
import { requestCustomerOtp, verifyCustomerOtp } from '../api/customer';
import { safeNavigate } from '../utils/safeNavigate';
import LandingHeader from '../components/landingSnapshot/LandingHeader.jsx';
import PublicSiteFooter from '../../components/Footer/PublicSiteFooter.jsx';

const normalizePhoneInput = (phoneInput, codeInput) => {
  const compactPhone = String(phoneInput || '').replace(/\s+/g, '');
  if (compactPhone.startsWith('+')) {
    const match = compactPhone.match(/^\+(\d{1,3})(\d{6,})$/);
    if (match) return { country_code: `+${match[1]}`, phone: match[2] };
  }
  const digitsOnly = compactPhone.replace(/\D/g, '');
  const rawCode = String(codeInput || '').trim();
  return { country_code: rawCode ? (rawCode.startsWith('+') ? rawCode : `+${rawCode}`) : '+91', phone: digitsOnly };
};

const toDigits = (value, maxLength) => String(value || '').replace(/\D/g, '').slice(0, maxLength);

const CustomerLoginView = ({ customerAuth }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextParam = searchParams.get('next');
  const redirectTo = nextParam && nextParam.startsWith('/') ? nextParam : '/';
  const [identifierType, setIdentifierType] = useState('phone');
  const [form, setForm] = useState({ country_code: '+91', phone: '', email: '', full_name: '', otp: '' });
  const [otpSent, setOtpSent] = useState(false);
  const [requiresSignup, setRequiresSignup] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { resendSeconds, resendBlocked, startResendCooldown, resetResendCooldown } = useOtpResendCooldown();

  useEffect(() => {
    if (!customerAuth.loading && customerAuth.customer) {
      safeNavigate(navigate, redirectTo, { replace: true });
    }
  }, [customerAuth.loading, customerAuth.customer, navigate, redirectTo]);

  const update = (key, value) => {
    const nextValue =
      key === 'phone'
        ? toDigits(value, 10)
        : key === 'country_code'
          ? `+${toDigits(value, 3) || '91'}`
          : key === 'otp'
            ? toDigits(value, 8)
            : value;
    setForm((prev) => ({ ...prev, [key]: nextValue, ...(key !== 'otp' ? { otp: '' } : {}) }));
    if (key !== 'otp') {
      setOtpSent(false);
      resetResendCooldown();
    }
    setError('');
    setNotice('');
  };

  const switchIdentifier = (next) => {
    setIdentifierType(next);
    setOtpSent(false);
    resetResendCooldown();
    setRequiresSignup(false);
    setForm((prev) => ({ ...prev, otp: '' }));
    setError('');
    setNotice('');
  };

  const buildPayload = () => {
    const phone = normalizePhoneInput(form.phone, form.country_code);
    return {
      identifier_type: identifierType,
      ...phone,
      email: form.email.trim() || undefined,
      full_name: form.full_name.trim() || undefined,
    };
  };

  const validateSignup = () => {
    const phone = normalizePhoneInput(form.phone, form.country_code);
    if (!form.full_name.trim()) return 'Full name is required for signup.';
    if (!/^\d{10}$/.test(phone.phone || '')) return 'Enter a valid 10-digit mobile number.';
    if (!form.email.includes('@')) return 'Valid email is required for signup.';
    return '';
  };

  const requestOtp = async () => {
    const signupError = requiresSignup ? validateSignup() : '';
    if (signupError) {
      setError(signupError);
      return;
    }
    const phone = normalizePhoneInput(form.phone, form.country_code);
    if (identifierType === 'phone' && !/^\d{10}$/.test(phone.phone || '')) {
      setError('Enter a valid 10-digit mobile number.');
      return;
    }
    if (identifierType === 'email' && !form.email.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }
    setSubmitting(true);
    setError('');
    setNotice('');
    try {
      const res = await requestCustomerOtp(buildPayload());
      setOtpSent(true);
      startResendCooldown();
      setRequiresSignup(Boolean(res.requiresSignup));
      setNotice(res.message || 'OTP sent.');
    } catch (err) {
      if (err.message?.toLowerCase().includes('signup')) {
        setRequiresSignup(true);
        setError('Account not found. Enter full name, mobile and email to sign up.');
      } else {
        setError(err.message || 'Unable to send OTP');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resendOtp = async () => {
    if (resendBlocked) return;
    await requestOtp();
  };

  const verifyOtp = async (event) => {
    event.preventDefault();
    if (!otpSent) {
      await requestOtp();
      return;
    }
    if (!String(form.otp || '').trim()) {
      setError('Enter OTP.');
      return;
    }
    const signupError = requiresSignup ? validateSignup() : '';
    if (signupError) {
      setError(signupError);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await verifyCustomerOtp({ ...buildPayload(), otp: String(form.otp).trim() });
      await customerAuth.applyAuth(res);
      safeNavigate(navigate, redirectTo, { replace: true });
    } catch (err) {
      setError(err.message || 'OTP verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <LandingHeader />
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-xl rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-[0_24px_70px_rgba(15,23,42,0.12)] md:p-8">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-emerald-700">Homevilas</p>
          <h1 className="mt-1 text-2xl font-semibold text-slate-950">Guest Login</h1>
          <p className="mt-1 text-sm text-slate-600">Login or signup with OTP. Password login has been removed.</p>

          {error && <div className="mt-4 rounded-lg bg-rose-50 text-rose-700 px-4 py-2 text-sm">{error}</div>}
          {notice && <div className="mt-4 rounded-lg bg-emerald-50 text-emerald-700 px-4 py-2 text-sm">{notice}</div>}

          <form className="mt-6 space-y-4" onSubmit={verifyOtp}>
            <div className="inline-flex rounded-full bg-slate-100 p-1">
              <button type="button" onClick={() => switchIdentifier('phone')} className={`rounded-full px-5 py-2 text-sm font-semibold transition ${identifierType === 'phone' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-700 hover:bg-white'}`}>Mobile OTP</button>
              <button type="button" onClick={() => switchIdentifier('email')} className={`rounded-full px-5 py-2 text-sm font-semibold transition ${identifierType === 'email' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-700 hover:bg-white'}`}>Email OTP</button>
            </div>

            {requiresSignup && (
              <input className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base outline-none transition focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100" value={form.full_name} onChange={(e) => update('full_name', e.target.value)} placeholder="Full name" required />
            )}
            <div className="flex gap-2">
              <input className="w-24 rounded-xl border border-slate-300 px-4 py-3 text-base outline-none transition focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100" value={form.country_code} onChange={(e) => update('country_code', e.target.value)} placeholder="+91" inputMode="numeric" maxLength={4} />
              <input className="flex-1 rounded-xl border border-slate-300 px-4 py-3 text-base outline-none transition focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="10-digit mobile number" required={requiresSignup || identifierType === 'phone'} inputMode="numeric" maxLength={10} pattern="[0-9]{10}" />
            </div>
            <input type="email" className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base outline-none transition focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder={identifierType === 'email' || requiresSignup ? 'Email address' : 'Email (optional)'} required={requiresSignup || identifierType === 'email'} />
            {otpSent && <input className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base outline-none transition focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100" value={form.otp} onChange={(e) => update('otp', e.target.value)} placeholder="Enter OTP" inputMode="numeric" maxLength={8} required />}
            <button type="submit" disabled={submitting} className="w-full rounded-xl bg-emerald-600 py-3 font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-70">
              {submitting ? 'Please wait...' : otpSent ? 'Verify OTP' : 'Send OTP'}
            </button>
            {otpSent && (
              <button
                type="button"
                onClick={resendOtp}
                disabled={submitting || resendSeconds > 0}
                className="w-full rounded-lg border border-slate-300 py-2 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {resendSeconds > 0 ? `Resend OTP in ${resendSeconds}s` : 'Resend OTP'}
              </button>
            )}
          </form>
        </div>
      </div>
      <PublicSiteFooter />
    </>
  );
};

const CustomerLoginWithAuth = () => {
  const customerAuth = useCustomerAuth();
  return <CustomerLoginView customerAuth={customerAuth} />;
};

const CustomerLogin = ({ customerAuth }) => (customerAuth ? <CustomerLoginView customerAuth={customerAuth} /> : <CustomerLoginWithAuth />);

export default CustomerLogin;
